import { setTenantTimeZone } from '@/time/tenantTime'
import { freshAccessToken, redirectToSignIn } from '@/auth/session-renewal'

/**
 * Minimal typed fetch wrapper for `/api/v1`. Errors arrive as the server's trilingual problem contract
 * (`{error, error_description, locale, ...args}`) and are surfaced as `ApiError` with the stable `code`.
 */
export interface ApiProblem {
  error: string
  error_description: string
  locale: string
  [arg: string]: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly problem: ApiProblem | null,
  ) {
    super(problem?.error_description ?? `${status} ${code}`)
  }
}

export interface ApiClientOptions {
  baseUrl?: string
  token?: () => string | null
  locale?: () => string
}

export function createApiClient(options: ApiClientOptions = {}) {
  const base = options.baseUrl ?? ''
  async function request<T>(method: string, path: string, body?: unknown, extraHeaders?: Record<string, string>, signal?: AbortSignal): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json', 'X-DstBfs-Capabilities': 'dual-label-v1', ...extraHeaders }
    // 临近过期先静默续期（单飞）；拿到的新令牌同步进 session store，再交给 options.token 读取
    const token = options.token ? ((await freshAccessToken()) ?? options.token()) : null
    if (token) headers['Authorization'] = `Bearer ${token}`
    const locale = options.locale?.()
    // An explicit captured locale (durable print retry) takes precedence over today's UI preference.
    if (locale && !headers['Accept-Language']) headers['Accept-Language'] = locale
    // multipart（文件导入）：Content-Type 交给浏览器带 boundary 自己定，不能手写
    const multipart = typeof FormData !== 'undefined' && body instanceof FormData
    if (body !== undefined && !multipart) headers['Content-Type'] = 'application/json'
    signal?.throwIfAborted()
    const res = await fetch(base + path, { method, headers, signal: signal ?? null, ...(body === undefined ? {} : { body: multipart ? body : JSON.stringify(body) }) })
    const zone = res.headers.get('X-Tenant-TimeZone')
    if (res.ok && zone) setTenantTimeZone(zone)
    if (res.status === 204) return undefined as T
    const text = await res.text()
    const json: unknown = text ? JSON.parse(text) : null
    if (!res.ok) {
      // DstBfs answers {error, error_description}; DstAi answers RFC 7807 {type, title, status} where `title` is the code — keep
      // both shapes so callers can map the code to a resource key (KR7.1 S09)
      const problem = json && typeof json === 'object' && ('error' in json || 'title' in json) ? (json as ApiProblem) : null
      // 401：续期也救不回来（refresh token 失效/会话超时）——直接跳登录、登录后回到本页，不把 401 摆给用户（业主 2026-10-10）。
      if (res.status === 401 && token) { redirectToSignIn() }
      throw new ApiError(res.status, problem?.error ?? problem?.['title'] ?? 'http_error', problem)
    }
    if (json && typeof json === 'object' && 'timeZoneId' in json && typeof json.timeZoneId === 'string') setTenantTimeZone(json.timeZoneId)
    return json as T
  }
  /**
   * 文件下载（O12-KR12.13 S09 报表 CSV 导出；O15-KR15.6 S12 存货导出 / 导入模板 / 结果报告 .xlsx）：与 request 同一套令牌续期、语言头、401 跳登录与问题契约；
   * 成功返回原始 Blob、服务端 Content-Disposition 里的文件名（RFC 5987 的 filename* 优先；无则 null）与响应头（如 `X-Export-Rows`），由调用方决定如何保存。
   */
  async function download(path: string): Promise<{ blob: Blob; filename: string | null; headers: Headers }> {
    const headers: Record<string, string> = { Accept: '*/*' }
    const token = options.token ? ((await freshAccessToken()) ?? options.token()) : null
    if (token) headers['Authorization'] = `Bearer ${token}`
    const locale = options.locale?.()
    if (locale) headers['Accept-Language'] = locale
    const res = await fetch(base + path, { method: 'GET', headers })
    if (!res.ok) {
      const text = await res.text()
      let problem: ApiProblem | null = null
      try { const json: unknown = text ? JSON.parse(text) : null; problem = json && typeof json === 'object' && ('error' in json || 'title' in json) ? (json as ApiProblem) : null } catch { problem = null }
      if (res.status === 401 && token) { redirectToSignIn() }
      throw new ApiError(res.status, problem?.error ?? problem?.['title'] ?? 'http_error', problem)
    }
    const cd = res.headers.get('Content-Disposition') ?? ''
    const star = /filename\*=UTF-8''([^;]+)/i.exec(cd)
    const plain = /filename="?([^";]+)"?/i.exec(cd)
    const filename = star?.[1] ? decodeURIComponent(star[1]) : (plain?.[1] ?? null)
    return { blob: await res.blob(), filename, headers: res.headers }
  }
  return {
    get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, undefined, signal),
    download,
    /** `headers` carries per-call headers such as `If-Match` (row_version) for optimistic-concurrency writes (KR12.10 master status). */
    post: <T>(path: string, body?: unknown, headers?: Record<string, string>) => request<T>('POST', path, body, headers),
    patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
    put: <T>(path: string, body?: unknown, headers?: Record<string, string>) => request<T>('PUT', path, body, headers),
    /** multipart/form-data POST（主数据文件导入 `/master/{kind}/import-file`，O15-KR15.6 S03）；`headers` 带 `Idempotency-Key`。 */
    postForm: <T>(path: string, form: FormData, headers?: Record<string, string>) => request<T>('POST', path, form, headers),
    /** DELETE without a body; used by revoke-style commands (KR12.10 S02 authorization grants) and inventory-item delete (If-Match). A 204 resolves to `undefined`. */
    delete: <T>(path: string, headers?: Record<string, string>) => request<T>('DELETE', path, undefined, headers),
  }
}

export type { Workspace, WorkspaceCategory, WorkspaceFunction, WorkspaceModule } from '@dstbfs/ui-shell'
